/**
 * Estados de tela do módulo Suprimentos — UM jeito de mostrar "carregando"
 * e "vazio" em todas as páginas e abas (pedido do dono, 2026-09-11).
 *
 * `Carregando`: esqueleto de tabela em veio de madeira (WoodSkeleton) com o
 * spinner e mensagens que giram — a pessoa vê que algo acontece, não um
 * "Carregando…" parado.
 *
 * `Vazio`: EmptyState do design system, em versão compacta para caber
 * dentro de um Card. Diz POR QUE está vazio e, quando faz sentido, O QUE
 * FAZER (ação primária).
 */
import React from "react";
import styled from "styled-components";
import { FiInbox } from "react-icons/fi";

import EmptyState from "../../../components/EmptyState";
import { MiniSpinner, WoodSkeleton, useRotatingMessages } from "../../../components/LoadingMarcenaria";

const MENSAGENS_SUPRIMENTOS = [
  "Conferindo o estoque...",
  "Contando as chapas...",
  "Buscando no almoxarifado...",
  "Quase lá...",
];

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 0;
`;

const Linha = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 24px;
  font-size: 13px;
  color: ${({ theme }) => theme.textSecondary};
  font-variant-numeric: tabular-nums;
`;

export function Carregando({ linhas = 5, colunas = 5, mensagens, mensagem }) {
  const lista = mensagem ? [mensagem] : mensagens || MENSAGENS_SUPRIMENTOS;
  const atual = useRotatingMessages(lista, 1800);
  return (
    <Shell role="status" aria-busy="true" aria-live="polite">
      <WoodSkeleton.Table rows={linhas} columns={colunas} />
      <Linha>
        <MiniSpinner size={14} label="Carregando" />
        <span>{atual}</span>
      </Linha>
    </Shell>
  );
}

const Compacto = styled.div`
  /* O EmptyState nasceu para página inteira; dentro de um Card ele fica
     mais baixo e sem borda dupla. */
  & > div {
    min-height: 160px;
    padding: 28px 20px;
    border: 1px dashed ${({ theme }) => theme.border};
    background: transparent;
  }
  & > div > div:first-child {
    width: 52px;
    height: 52px;
  }
  & > div > div:first-child svg {
    width: 26px;
    height: 26px;
  }
`;

export function Vazio({ titulo, descricao, tone = "info", acoes = [], icone = FiInbox, children }) {
  return (
    <Compacto>
      <EmptyState tone={tone} titulo={titulo} descricao={descricao} acoes={acoes} icone={icone}>
        {children}
      </EmptyState>
    </Compacto>
  );
}

/** Vazio por causa de FILTRO: o dado existe, o recorte é que não trouxe nada. */
export function VazioFiltro({ titulo = "Nada passa nos filtros atuais", descricao, onLimpar }) {
  return (
    <Vazio
      tone="search"
      titulo={titulo}
      descricao={descricao || "Afrouxe a busca ou limpe os filtros para ver tudo de novo."}
      acoes={onLimpar ? [{ label: "Limpar filtros", onClick: onLimpar }] : []}
    />
  );
}

const Inline = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 18px 12px;
  font-size: 12.5px;
  color: ${({ theme }) => theme.textSecondary};
  border: 1px dashed ${({ theme }) => theme.border};
  border-radius: 10px;
  svg { opacity: 0.7; flex-shrink: 0; }
`;

/** Vazio em miniatura, para widgets e blocos pequenos (painéis, cards). */
export function VazioInline({ children, icone: Icone = FiInbox }) {
  return (
    <Inline role="status">
      <Icone size={14} />
      <span>{children}</span>
    </Inline>
  );
}
